import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../../util/log';
import { storableError } from '../../util/errors';

// ================ Async Thunks ================ //

export const recoverPassword = createAsyncThunk(
  'passwordRecoveryPage/recoverPassword',
  ({ email }, { rejectWithValue, extra: sdk }) => {
    return sdk.passwordReset
      .request({ email })
      .then(() => ({ email }))
      .catch(e => {
        // Story 9.18 AC3 — a failed password recovery used to log nothing at
        // all. EXACTLY TWO ARGUMENTS: log.error's third argument is forwarded
        // verbatim to Sentry as issue "extra" (util/log.js:385-393). The
        // `email` below stays in the Redux payload — the page reads it back to
        // re-display the address — and is never passed to log.error.
        log.error(e, 'password-recovery-failed');
        return rejectWithValue({ error: storableError(e), email });
      });
  }
);

// ================ Slice ================ //

const passwordRecoveryPageSlice = createSlice({
  name: 'passwordRecoveryPage',
  initialState: {
    initialEmail: null,
    submittedEmail: null,
    recoveryError: null,
    recoveryInProgress: false,
    passwordRequested: false,
  },
  reducers: {
    retypePasswordRecoveryEmail: state => {
      state.initialEmail = state.submittedEmail;
      state.submittedEmail = null;
      state.passwordRequested = false;
    },
    clearPasswordRecoveryError: state => {
      state.recoveryError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(recoverPassword.pending, state => {
        state.submittedEmail = null;
        state.recoveryInProgress = true;
        state.recoveryError = null;
      })
      .addCase(recoverPassword.fulfilled, (state, action) => {
        const { email } = action.payload;
        state.submittedEmail = email;
        state.initialEmail = email;
        state.recoveryInProgress = false;
        state.passwordRequested = true;
      })
      .addCase(recoverPassword.rejected, (state, action) => {
        const { error, email } = action.payload;
        state.recoveryInProgress = false;
        state.recoveryError = error;
        state.initialEmail = email;
      });
  },
});

export const {
  retypePasswordRecoveryEmail,
  clearPasswordRecoveryError,
} = passwordRecoveryPageSlice.actions;

export default passwordRecoveryPageSlice.reducer;
